import { Doc } from '@dadohr/shared/domainTypes/document';
import { isServiceEnabledInConfig } from '@dadohr/shared/domainTypes/globalConfig';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  Card,
  CardContent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React from 'react';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { Section } from '../../../../../../../layout/Section';
import {
  setOrgServicePaused,
  useGlobalConfig
} from '../../../../../GlobalConfig';
import {
  isOrgPaused,
  PER_ORG_PAUSABLE_SERVICES
} from '../../../../../GlobalConfig/disabledOrgs';

type Props = {
  org: Doc<IOrganization>;
};

/**
 * Per-org pause for schedules / automations, layered on the global
 * kill-switches (Global Config page). Pausing keeps due items PENDING; they
 * send on the next cron tick after the org is resumed.
 */
export const Dispatch: React.FC<Props> = ({ org }) => {
  const [config] = useGlobalConfig();
  const { enqueueSnackbar } = useSnackbar();
  if (!config) {
    return null;
  }

  return (
    <Section>
      <CanvasBar>Dispatch</CanvasBar>
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Pause sending for this org only. Due items stay pending and send once
            the org is resumed.
          </Typography>
          <Table>
            <TableBody>
              {PER_ORG_PAUSABLE_SERVICES.map(({ key, label }) => {
                const enabledForOrg = !isOrgPaused(
                  config.disabledOrgs,
                  key,
                  org.id
                );
                const globallyEnabled = isServiceEnabledInConfig(config, key);
                const effective = isServiceEnabledInConfig(config, key, org.id);
                return (
                  <TableRow key={key}>
                    <TableCell>{label}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={effective ? 'textPrimary' : 'error'}
                      >
                        {effective ? 'Sending' : 'Paused'}
                        {!globallyEnabled &&
                          ' — paused globally (Global Config); this org setting has no effect until the global switch is on'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Switch
                        checked={enabledForOrg}
                        inputProps={{
                          'aria-label': `${label} enabled for ${org.data.name}`
                        }}
                        onChange={(ev) =>
                          setOrgServicePaused(
                            key,
                            org.id,
                            !ev.target.checked
                          ).catch(() =>
                            enqueueSnackbar(`Failed to update ${label}.`, {
                              variant: 'error'
                            })
                          )
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Section>
  );
};
